/**
 * Tiny procedural chiptune synth. Works against any BaseAudioContext, so the
 * same code renders live through an AudioContext or offline through an
 * OfflineAudioContext (used by the WAV preview harness).
 *
 * All voices are additive-only (no samples/assets): square/pulse (with
 * adjustable duty cycle via a hand-built PeriodicWave), triangle, and
 * white-noise, each shaped with a short ADSR-ish envelope on a per-note
 * GainNode so notes never click.
 */

export type ToneShape = 'square' | 'triangle' | 'sawtooth';

export interface ToneOpts {
  /** Peak linear gain of the envelope (post-oscillator). Keep this small —
   * the master chain applies overall volume on top. */
  gain?: number;
  attack?: number;
  decay?: number;
  /** Sustain level as a fraction of peak gain (0..1). */
  sustain?: number;
  release?: number;
  /** Pulse duty cycle in (0, 1), only used for shape 'square'. 0.5 = a plain
   * square wave; smaller values = a thinner, more nasal pulse (classic NES
   * lead timbre). Omit for a plain square oscillator. */
  duty?: number;
  /** Optional per-voice detune in cents (adds a little chiptune "chorus"). */
  detune?: number;
}

const MIN_GAIN = 0.0001;

const pulseWaveCache = new WeakMap<BaseAudioContext, Map<number, PeriodicWave>>();

function getPulseWave(ctx: BaseAudioContext, duty: number): PeriodicWave {
  let byDuty = pulseWaveCache.get(ctx);
  if (!byDuty) {
    byDuty = new Map();
    pulseWaveCache.set(ctx, byDuty);
  }
  const key = Math.round(duty * 1000);
  let wave = byDuty.get(key);
  if (!wave) {
    const harmonics = 20;
    const real = new Float32Array(harmonics);
    const imag = new Float32Array(harmonics);
    for (let n = 1; n < harmonics; n++) {
      // Fourier series of a duty-cycle pulse train.
      real[n] = (2 / (n * Math.PI)) * Math.sin(n * Math.PI * duty);
    }
    wave = ctx.createPeriodicWave(real, imag, { disableNormalization: false });
    byDuty.set(key, wave);
  }
  return wave;
}

/** Schedule one tonal note (square/pulse, triangle, or sawtooth) starting at
 * `time` (AudioContext clock seconds), lasting `dur` seconds, into `dest`. */
export function playTone(
  ctx: BaseAudioContext,
  dest: AudioNode,
  time: number,
  freq: number,
  dur: number,
  shape: ToneShape,
  opts: ToneOpts = {},
): void {
  const { gain = 0.18, attack = 0.006, decay = 0.05, sustain = 0.55, release = 0.06, duty, detune } = opts;
  const osc = ctx.createOscillator();
  if (shape === 'square' && duty !== undefined && duty !== 0.5) {
    osc.setPeriodicWave(getPulseWave(ctx, duty));
  } else {
    osc.type = shape;
  }
  osc.frequency.setValueAtTime(Math.max(1, freq), time);
  if (detune) osc.detune.setValueAtTime(detune, time);

  const env = ctx.createGain();
  osc.connect(env);
  env.connect(dest);

  const peak = Math.max(gain, MIN_GAIN);
  const sustainLevel = Math.max(peak * sustain, MIN_GAIN);
  const relStart = Math.max(time + attack + decay, time + dur - release);

  env.gain.setValueAtTime(MIN_GAIN, time);
  env.gain.exponentialRampToValueAtTime(peak, time + attack);
  env.gain.exponentialRampToValueAtTime(sustainLevel, time + attack + decay);
  env.gain.setValueAtTime(sustainLevel, relStart);
  env.gain.exponentialRampToValueAtTime(MIN_GAIN, relStart + release);

  osc.start(time);
  osc.stop(relStart + release + 0.02);
}

export interface NoiseOpts {
  gain?: number;
  attack?: number;
  release?: number;
  /** Lowpass/highpass/bandpass the noise to sculpt hats/thuds/bursts. */
  filterHz?: number;
  filterType?: BiquadFilterType;
  filterQ?: number;
}

const noiseBufferCache = new WeakMap<BaseAudioContext, AudioBuffer>();

function getNoiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  let buf = noiseBufferCache.get(ctx);
  if (!buf) {
    // 2s of white noise is plenty to loop/slice from for any short burst.
    const len = Math.max(1, Math.floor(ctx.sampleRate * 2));
    buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    noiseBufferCache.set(ctx, buf);
  }
  return buf;
}

/** Schedule one noise burst (hats, thuds, death/destruction hits) starting
 * at `time`, lasting `dur` seconds, into `dest`. */
export function playNoise(ctx: BaseAudioContext, dest: AudioNode, time: number, dur: number, opts: NoiseOpts = {}): void {
  const { gain = 0.15, attack = 0.001, release, filterHz, filterType = 'lowpass', filterQ = 0.7 } = opts;
  const src = ctx.createBufferSource();
  src.buffer = getNoiseBuffer(ctx);
  src.loop = false;
  const startOffset = Math.random() * 1.5; // vary which slice of the buffer plays

  let node: AudioNode = src;
  if (filterHz) {
    const filt = ctx.createBiquadFilter();
    filt.type = filterType;
    filt.frequency.value = filterHz;
    filt.Q.value = filterQ;
    src.connect(filt);
    node = filt;
  }

  const env = ctx.createGain();
  node.connect(env);
  env.connect(dest);

  const peak = Math.max(gain, MIN_GAIN);
  const rel = release ?? dur * 0.7;
  env.gain.setValueAtTime(MIN_GAIN, time);
  env.gain.exponentialRampToValueAtTime(peak, time + attack);
  env.gain.exponentialRampToValueAtTime(MIN_GAIN, time + attack + rel);

  src.start(time, startOffset, dur + 0.05);
  src.stop(time + dur + 0.06);
}
